/**
 * Blog Service - PostgreSQL Implementation
 * Migrated from Supabase to self-hosted PostgreSQL
 * 
 * MONOLITHIC ARCHITECTURE: API and frontend served from same origin
 */

import { blogPosts as staticBlogPosts, calculateReadTime } from '@/data/blogPosts';
import type { BlogPost } from '@/data/blogPosts';

// API base URL - uses relative path for monolithic deployment
const API_BASE_URL = '/api';

export async function fetchBlogPosts(): Promise<BlogPost[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/blog-posts`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data || data.length === 0) {
      return staticBlogPosts;
    }

    // Merge: DB posts first, then static posts not already in DB
    const dbSlugs = new Set(data.map((p: any) => p.slug));
    const staticOnly = staticBlogPosts.filter((p) => !dbSlugs.has(p.slug));

    const dbPosts: BlogPost[] = data.map((p: any) => ({
      slug: p.slug,
      title: p.title,
      excerpt: p.excerpt,
      category: p.category,
      readTime: calculateReadTime(p.content),
      image: p.image || '/images/blog/default.jpg',
      imageAlt: p.image_alt || p.title,
      seoKeywords: p.seo_keywords,
      date: p.date,
      author: p.author,
      content: p.content,
    }));

    return [...dbPosts, ...staticOnly];
  } catch (error) {
    console.warn('Failed to fetch blog posts from API, using static fallback:', error);
    return staticBlogPosts;
  }
}

export async function fetchBlogPostBySlug(slug: string): Promise<BlogPost | undefined> {
  try {
    const response = await fetch(`${API_BASE_URL}/blog-posts/${encodeURIComponent(slug)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return staticBlogPosts.find((p) => p.slug === slug);
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      slug: data.slug,
      title: data.title,
      excerpt: data.excerpt,
      category: data.category,
      readTime: calculateReadTime(data.content),
      image: data.image || '/images/blog/default.jpg',
      imageAlt: data.image_alt || data.title,
      seoKeywords: data.seo_keywords,
      date: data.date,
      author: data.author,
      content: data.content,
    };
  } catch (error) {
    console.warn('Failed to fetch blog post from API, using static fallback:', error);
    return staticBlogPosts.find((p) => p.slug === slug);
  }
}
